import { useTranslation } from 'react-i18next';
import type {
  ReconciliationAdjustmentMode,
  ReconciliationAdjustmentPreviewEvidence,
  ReconciliationCaseDto,
  ReconciliationDecisionDto,
} from '@shared/api/contracts/reconciliation';
import { formatDateTime } from '@shared/lib/format-date';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui';

interface DiscrepancyAdjustmentDialogProps {
  open: boolean;
  loading?: boolean;
  caseDetail: ReconciliationCaseDto;
  walletId: string;
  amount: string;
  mode: ReconciliationAdjustmentMode;
  evidenceRef: string;
  reason: string;
  preview: ReconciliationDecisionDto | null;
  canConfirm?: boolean;
  onOpenChange: (open: boolean) => void;
  onWalletIdChange: (value: string) => void;
  onAmountChange: (value: string) => void;
  onModeChange: (value: ReconciliationAdjustmentMode) => void;
  onEvidenceRefChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onPreview: () => void;
  onConfirm: () => void;
}

const readPreviewEvidence = (decision: ReconciliationDecisionDto | null): ReconciliationAdjustmentPreviewEvidence | null => {
  if (!decision?.evidence || typeof decision.evidence !== 'object') return null;
  return decision.evidence as ReconciliationAdjustmentPreviewEvidence;
};

export function DiscrepancyAdjustmentDialog({
  open,
  loading = false,
  caseDetail,
  walletId,
  amount,
  mode,
  evidenceRef,
  reason,
  preview,
  canConfirm = true,
  onOpenChange,
  onWalletIdChange,
  onAmountChange,
  onModeChange,
  onEvidenceRefChange,
  onReasonChange,
  onPreview,
  onConfirm,
}: DiscrepancyAdjustmentDialogProps) {
  const { t } = useTranslation('cabinet');
  const walletNumeric = Number.parseInt(walletId.trim(), 10);
  const evidenceRefs = evidenceRef
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => item.length >= 3);
  const formValid =
    Number.isSafeInteger(walletNumeric) &&
    walletNumeric > 0 &&
    /^-?\d+(\.\d+)?$/.test(amount.trim()) &&
    evidenceRefs.length > 0 &&
    reason.trim().length >= 3;
  const previewEvidence = readPreviewEvidence(preview);
  const previewExpiresAt = previewEvidence?.expiresAt ?? preview?.expiresAt ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('reconciliation.case.adjustment.title')}</DialogTitle>
          <DialogDescription>{t('reconciliation.case.adjustment.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            {caseDetail.assetCode} / {caseDetail.networkCode} · {caseDetail.scopeKey}
          </p>
          <div className="space-y-2">
            <Label htmlFor="adjustment-wallet">{t('reconciliation.case.adjustment.walletId')}</Label>
            <Input
              id="adjustment-wallet"
              inputMode="numeric"
              value={walletId}
              className="font-mono"
              onChange={(event) => onWalletIdChange(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjustment-mode">{t('reconciliation.case.adjustment.mode')}</Label>
            <Select value={mode} onValueChange={(value) => onModeChange(value as ReconciliationAdjustmentMode)}>
              <SelectTrigger id="adjustment-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RESTORE_PROJECTION">{t('reconciliation.case.adjustment.modeRestore')}</SelectItem>
                <SelectItem value="RECORD_MISSING_ENTRY">{t('reconciliation.case.adjustment.modeRecord')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjustment-amount">{t('reconciliation.case.adjustment.amount')}</Label>
            <Input
              id="adjustment-amount"
              value={amount}
              className="font-mono tabular-nums"
              onChange={(event) => onAmountChange(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('reconciliation.case.adjustment.amountHint')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjustment-evidence">{t('reconciliation.case.adjustment.evidence')}</Label>
            <Input
              id="adjustment-evidence"
              value={evidenceRef}
              placeholder="ui:ticket-123"
              onChange={(event) => onEvidenceRefChange(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjustment-reason">{t('reconciliation.enqueue.reason')}</Label>
            <Input id="adjustment-reason" value={reason} onChange={(event) => onReasonChange(event.target.value)} />
          </div>
          {preview && previewEvidence ? (
            <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3 sm:rounded-2xl">
              <p className="font-medium text-foreground">{t('reconciliation.case.adjustment.previewTitle')}</p>
              <dl className="grid gap-1 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">{t('reconciliation.case.adjustment.bookedBefore')}</dt>
                  <dd className="font-mono tabular-nums">{previewEvidence.bookedBefore ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">{t('reconciliation.case.adjustment.balanceBefore')}</dt>
                  <dd className="font-mono tabular-nums">{previewEvidence.balanceBefore ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">{t('reconciliation.case.adjustment.previewAmount')}</dt>
                  <dd className="font-mono tabular-nums">{previewEvidence.amount ?? '—'}</dd>
                </div>
              </dl>
              <p className="text-xs text-muted-foreground">
                {previewExpiresAt
                  ? t('reconciliation.case.adjustment.previewExpiryUntil', { time: formatDateTime(previewExpiresAt) })
                  : t('reconciliation.case.adjustment.previewExpiryFallback')}
              </p>
            </div>
          ) : null}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            {t('withdrawals.actions.back')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:w-auto"
            loading={loading && !preview}
            disabled={!formValid || Boolean(preview)}
            onClick={onPreview}
          >
            {t('reconciliation.case.adjustment.previewAction')}
          </Button>
          <Button
            type="button"
            className="w-full sm:w-auto"
            loading={loading && Boolean(preview)}
            disabled={!preview || !canConfirm}
            onClick={onConfirm}
          >
            {t('reconciliation.case.adjustment.confirmAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
